/**
 * Default nominal cadence (ms). Matches {@link PLAYBACK_HEARTBEAT_INTERVAL_MS} in `@/constants`
 * without importing that module (avoids `__APP_VERSION__` in Jest).
 */
export const PLAYBACK_HEARTBEAT_JITTER_DEFAULT_BASE_MS = 60_000

/**
 * ±10% around the nominal interval → factor in `[0.9, 1.1)` when `random()` is in `[0, 1)`.
 * Keeps cadence well above the API rate-limit budget (~300/min).
 */
export const PLAYBACK_HEARTBEAT_JITTER_MIN_FACTOR = 0.9
export const PLAYBACK_HEARTBEAT_JITTER_SPAN = 0.2

/**
 * Effective heartbeat send interval: `baseMs * (0.9 + random * 0.2)`.
 * Stable per cadence arm — de-synchronizes herd arrivals without re-rolling every tick.
 */
export function nextPlaybackHeartbeatIntervalMs(
  baseMs: number = PLAYBACK_HEARTBEAT_JITTER_DEFAULT_BASE_MS,
  random: () => number = Math.random
): number {
  const factor = PLAYBACK_HEARTBEAT_JITTER_MIN_FACTOR + unitRandom(random) * PLAYBACK_HEARTBEAT_JITTER_SPAN
  return Math.max(1, Math.round(baseMs * factor))
}

/** Random delay before the first send tick in `[0, baseMs)`. */
export function playbackHeartbeatInitialPhaseMs(
  baseMs: number = PLAYBACK_HEARTBEAT_JITTER_DEFAULT_BASE_MS,
  random: () => number = Math.random
): number {
  return Math.floor(unitRandom(random) * baseMs)
}

export type PlaybackHeartbeatCadenceHandle = {
  /** True while phase timeout or interval is scheduled. */
  isArmed: () => boolean
  clear: () => void
}

type ArmPlaybackHeartbeatCadenceOptions = {
  onTick: () => void
  baseIntervalMs?: number
  random?: () => number
  setTimeoutFn?: typeof setTimeout
  setIntervalFn?: typeof setInterval
  clearTimeoutFn?: typeof clearTimeout
  clearIntervalFn?: typeof clearInterval
}

/**
 * Arms a de-herded heartbeat cadence: random phase, then fixed jittered interval.
 * Does **not** fire synchronously on arm (phase may be 0 → first tick on next macrotask).
 */
export function armPlaybackHeartbeatCadence(
  options: ArmPlaybackHeartbeatCadenceOptions
): PlaybackHeartbeatCadenceHandle {
  const baseMs = options.baseIntervalMs ?? PLAYBACK_HEARTBEAT_JITTER_DEFAULT_BASE_MS
  const random = options.random ?? Math.random
  const setTimeoutFn = options.setTimeoutFn ?? setTimeout
  const setIntervalFn = options.setIntervalFn ?? setInterval
  const clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout
  const clearIntervalFn = options.clearIntervalFn ?? clearInterval

  const intervalMs = nextPlaybackHeartbeatIntervalMs(baseMs, random)
  const phaseMs = playbackHeartbeatInitialPhaseMs(baseMs, random)

  let phaseTimeout: ReturnType<typeof setTimeout> | null = null
  let intervalTimer: ReturnType<typeof setInterval> | null = null

  const clear = () => {
    if (phaseTimeout != null) {
      clearTimeoutFn(phaseTimeout)
      phaseTimeout = null
    }
    if (intervalTimer != null) {
      clearIntervalFn(intervalTimer)
      intervalTimer = null
    }
  }

  phaseTimeout = setTimeoutFn(() => {
    phaseTimeout = null
    options.onTick()
    intervalTimer = setIntervalFn(() => {
      options.onTick()
    }, intervalMs)
  }, phaseMs)

  return {
    isArmed: () => phaseTimeout != null || intervalTimer != null,
    clear
  }
}

function unitRandom(random: () => number): number {
  const n = random()
  if (!Number.isFinite(n)) {
    return 0
  }
  if (n <= 0) {
    return 0
  }
  if (n >= 1) {
    return 0.9999999999999999
  }
  return n
}
